<template>
    <!-- Comparison: overlays one reference Production Group at a time onto the chart currently in
         view. Pick-one, never multi-select - selecting a candidate replaces whichever reference
         was already showing rather than adding to it. Disabled (with a stated reason) when the
         control is unavailable, and shows an explicit empty state when the Section has neither a
         preceding Production Group nor a favourited one to offer - both take the same visual slot as
         the search input so the control's width never jumps between states. -->
    <div class="flex w-64 shrink-0 items-center gap-2">
        <span class="shrink-0 text-xs font-medium text-gray-600">{{ $t('views.monitoring.activeGroups.modals.productivity.compareHeading') }}</span>
        <!-- A pick that is standing and being drawn. The same chip goes muted below when the range
             takes the control away: the pick is kept rather than dropped, so the chip has to say
             which of the two it is - a reference named in the accent colour while nothing is drawn
             on the chart is the control contradicting the chart. -->
        <div
            v-if="selected"
            class="flex min-w-0 items-center gap-1 rounded-full px-2 py-1 text-xs"
            :class="held ? 'bg-gray-100 text-gray-500' : 'bg-primary-300 text-primary-800'"
        >
            <span class="truncate">{{ selected.name }}</span>
            <span v-if="held" class="inline-flex shrink-0 cursor-help" :title="heldReason">
                <Info class="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <button
                type="button"
                class="shrink-0"
                :class="held ? 'text-gray-400 hover:text-gray-600' : 'text-primary-700 hover:text-primary-800'"
                :title="$t('views.monitoring.activeGroups.modals.productivity.compareClear')"
                @click="emit('reference-picked', null)"
            >
                &times;
            </button>
        </div>
        <!-- Unavailable and empty state as a short phrase plus an info icon carrying the sentence,
             rather than the sentence itself clipped by the toolbar. Truncated, it reads as a bug
             ("Intet tidligere eller favorit…") and says nothing; spelled out in full it pushes the
             granularity group off the line. Neither is something the reader acts on - both explain
             why there is nothing here to pick. -->
        <span v-else-if="unavailableReason || candidates.length === 0" class="inline-flex min-w-0 flex-1 items-center gap-1 text-xs italic text-gray-400">
            <span class="truncate">{{ disabledSummary }}</span>
            <!-- Wrapped, because a `title` attribute on an <svg> is inert - only a <title> child
                 counts there - so the reason would never actually surface. -->
            <span class="inline-flex shrink-0 cursor-help not-italic" :title="disabledReason">
                <Info class="h-3.5 w-3.5" aria-hidden="true" />
            </span>
        </span>
        <AutoComplete
            v-else
            class="min-w-0 flex-1"
            :items="candidates"
            item-text="name"
            item-value="productionGroupId"
            :label="$t('views.monitoring.activeGroups.modals.productivity.comparePickerLabel')"
            @item-clicked="onItemClicked"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Info } from 'lucide-vue-next';
import AutoComplete from '@/components/ui/AutoComplete.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import type { ComparisonCandidate } from './productivityComparison';

const props = defineProps<{
    candidates: ComparisonCandidate[];
    selected: ComparisonCandidate | null;
    // Set (with the reason to display) when the range spans several Production Groups - the real
    // periods are already on the axis then, so overlaying one of them again would draw it twice.
    unavailableReason: string | null;
}>();

const emit = defineEmits<{
    'reference-picked': [candidate: ComparisonCandidate | null];
}>();

// Whether the pick is being kept rather than drawn - the range covers several Production Groups, so the
// control is unavailable while the selection itself still stands.
const held = computed(() => props.selected !== null && props.unavailableReason !== null);

// Why the chip is muted: the reason the control is unavailable, plus what is being done about the
// pick. The first half alone would leave a reader wondering whether their reference had been thrown
// away, which is the question a greyed-out chip with an X on it actually raises.
const heldReason = computed(
    () => `${props.unavailableReason} ${i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareHeldReason')}`
);

// The reason the picker is not offered: the range spanning several Production Groups takes precedence
// over the Section simply having nothing to compare against, since it is the narrower explanation.
const disabledReason = computed(
    () => props.unavailableReason ?? (i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareEmptyState') as string)
);

// The few words that fit on the toolbar; disabledReason above is the sentence behind the icon.
const disabledSummary = computed(() =>
    props.unavailableReason !== null
        ? (i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareUnavailableShort') as string)
        : (i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareEmptyShort') as string)
);

function onItemClicked(item: ComparisonCandidate): void {
    emit('reference-picked', item);
}
</script>
